import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ButtonModule, CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { TextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { SkeletonModule } from '@syncfusion/ej2-angular-notifications';
import { Subscription } from 'rxjs';
import { RequestErrorPolicyService } from '../error/request-error-policy.service';
import { FormControlErrorComponent } from '../shared/form-control-error/form-control-error.component';
import { FormFieldLabelComponent } from '../shared/form-field-label/form-field-label.component';
import { ListingLayoutComponent } from '../shared/listing-layout/listing-layout.component';
import { PanelComponent } from '../shared/panel/panel.component';
import { ToastService } from '../shared/toast/toast.service';
import { MyAccountActivityLogDto, MyAccountProfileDto } from './my-account.models';
import { MyAccountService } from './my-account.service';

@Component({
  selector: 'app-my-account',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ListingLayoutComponent,
    PanelComponent,
    FormControlErrorComponent,
    FormFieldLabelComponent,
    TextBoxModule,
    ButtonModule,
    CheckBoxModule,
    SkeletonModule,
  ],
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyAccountComponent implements OnInit, OnDestroy {
  isLoading = true;
  isSaving = false;
  profile: MyAccountProfileDto | null = null;
  activityLogs: MyAccountActivityLogDto[] = [];
  saveMessage = '';
  errorMessage = '';
  
  readonly emailErrorMessages = {
    required: 'Informe um e-mail válido.',
    email: 'Informe um e-mail válido.',
  };
  readonly currentPasswordErrorMessages = {
    currentPasswordRequired: 'Informe a senha atual para confirmar a alteração.',
  };
  readonly newPasswordErrorMessages = {
    required: 'Informe uma senha para concluir o cadastro.',
    minlength: 'Mínimo de 8 caracteres.',
  };
  readonly confirmPasswordErrorMessages = {
    required: 'Confirme a senha para concluir o cadastro.',
    passwordMismatch: 'A confirmação da nova senha precisa ser igual ao valor informado acima.',
  };

  readonly form: FormGroup;
  private readonly subs = new Subscription();

  constructor(
    private readonly fb: FormBuilder,
    private readonly myAccountService: MyAccountService,
    private readonly toastService: ToastService,
    private readonly requestErrorPolicy: RequestErrorPolicyService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.form = this.fb.group(
      {
        basicInfo: this.fb.group({
          name: [{ value: '', disabled: true }],
          birthDate: [{ value: '', disabled: true }],
          city: [{ value: '', disabled: true }],
          state: [{ value: '', disabled: true }],
          department: [{ value: '', disabled: true }],
          role: [{ value: '', disabled: true }],
          employeeCode: [{ value: '', disabled: true }],
        }),
        access: this.fb.group({
          enableEmailEdit: [false],
          email: [{ value: '', disabled: true }, [Validators.required, Validators.email]],
        }),
        security: this.fb.group({
          enablePasswordEdit: [false],
          currentPassword: [{ value: '', disabled: true }],
          newPassword: [{ value: '', disabled: true }, [Validators.minLength(8)]],
          confirmNewPassword: [{ value: '', disabled: true }],
        }),
      },
      {
        validators: [(control: AbstractControl) => this.passwordChangeValidator(control)],
      },
    );
  }

  ngOnInit(): void {
    this.registerEditStateSubscriptions();
    this.loadProfile();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get emailControl(): AbstractControl | null {
    return this.form.get('access.email');
  }

  get enableEmailEditControl(): AbstractControl | null {
    return this.form.get('access.enableEmailEdit');
  }

  get enablePasswordEditControl(): AbstractControl | null {
    return this.form.get('security.enablePasswordEdit');
  }

  get currentPasswordControl(): AbstractControl | null {
    return this.form.get('security.currentPassword');
  }

  get newPasswordControl(): AbstractControl | null {
    return this.form.get('security.newPassword');
  }

  get confirmNewPasswordControl(): AbstractControl | null {
    return this.form.get('security.confirmNewPassword');
  }

  get lastPasswordChangeLabel(): string {
    if (!this.profile?.lastPasswordChangeAt) {
      return 'Sem informação disponível';
    }

    return new Date(this.profile.lastPasswordChangeAt).toLocaleString('pt-BR');
  }

  get isEmailEditEnabled(): boolean {
    return !!this.enableEmailEditControl?.value;
  }

  get isPasswordEditEnabled(): boolean {
    return !!this.enablePasswordEditControl?.value;
  }

  get hasPasswordConfigured(): boolean {
    return this.profile?.hasPasswordConfigured ?? true;
  }

  get isCredentialRegistrationRequired(): boolean {
    return !this.hasPasswordConfigured;
  }

  get shouldShowPasswordFields(): boolean {
    return this.isPasswordEditEnabled;
  }

  get shouldRequireCurrentPassword(): boolean {
    return this.isPasswordEditEnabled && this.hasPasswordConfigured;
  }

  onCancel(): void {
    if (!this.profile) {
      return;
    }

    this.applyProfileToForm(this.profile);
    this.clearFeedback();
  }

  onSave(): void {
    this.clearFeedback();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMessage = 'Revise os campos destacados antes de salvar as alterações.';
      this.cdr.markForCheck();
      return;
    }

    const email = this.isEmailEditEnabled
      ? (this.emailControl?.value ?? '').toString().trim()
      : (this.profile?.email ?? '').toString().trim();
    const currentPassword = this.isPasswordEditEnabled
      ? (this.currentPasswordControl?.value ?? '').toString()
      : '';
    const newPassword = this.isPasswordEditEnabled
      ? (this.newPasswordControl?.value ?? '').toString()
      : '';

    this.isSaving = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.myAccountService.updateMyAccount({
        email,
        currentPassword,
        newPassword,
      }).subscribe({
        next: (result) => {
          this.profile = result.profile;
          this.activityLogs = result.activityLogs;
          this.applyProfileToForm(result.profile);
          this.saveMessage = result.message;
          this.toastService.success(result.message);
          this.isSaving = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.errorMessage = this.requestErrorPolicy.resolveNonHttpMessage(
            error,
            'Não foi possível salvar suas alterações no momento.',
          );

          this.isSaving = false;
          this.cdr.markForCheck();
        },
      }),
    );
  }

  private loadProfile(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.myAccountService.getMyAccountProfile().subscribe({
        next: (viewData) => {
          this.profile = viewData.profile;
          this.activityLogs = viewData.activityLogs;
          this.applyProfileToForm(viewData.profile);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.errorMessage = this.requestErrorPolicy.resolveNonHttpMessage(
            error,
            'Não foi possível carregar os dados da sua conta agora.',
          );

          this.isLoading = false;
          this.cdr.markForCheck();
        },
      }),
    );
  }

  private applyProfileToForm(profile: MyAccountProfileDto): void {
    const enableEmailEdit = profile.hasPasswordConfigured ? !profile.email?.trim() : true;
    const enablePasswordEdit = profile.hasPasswordConfigured ? false : true;

    this.form.patchValue(
      {
        basicInfo: {
          name: profile.name,
          birthDate: new Date(profile.birthDate).toLocaleDateString('pt-BR'),
          city: profile.city,
          state: profile.state,
          department: profile.department,
          role: profile.role,
          employeeCode: profile.employeeCode,
        },
        access: {
          enableEmailEdit,
          email: profile.email,
        },
        security: {
          enablePasswordEdit,
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: '',
        },
      },
      { emitEvent: false },
    );

    this.configureCredentialRegistrationState(profile.hasPasswordConfigured);
    this.updateEmailEditState(enableEmailEdit, false);
    this.updatePasswordEditState(enablePasswordEdit, false);
    this.form.updateValueAndValidity({ emitEvent: false });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private clearFeedback(): void {
    this.saveMessage = '';
    this.errorMessage = '';
  }

  private registerEditStateSubscriptions(): void {
    this.subs.add(
      this.enableEmailEditControl?.valueChanges.subscribe((enabled) => {
        this.updateEmailEditState(!!enabled);
      }) ?? Subscription.EMPTY,
    );

    this.subs.add(
      this.enablePasswordEditControl?.valueChanges.subscribe((enabled) => {
        this.updatePasswordEditState(!!enabled);
      }) ?? Subscription.EMPTY,
    );
  }

  private configureCredentialRegistrationState(hasPasswordConfigured: boolean): void {
    if (!this.enableEmailEditControl || !this.enablePasswordEditControl) {
      return;
    }

    if (hasPasswordConfigured) {
      this.enableEmailEditControl.enable({ emitEvent: false });
      this.enablePasswordEditControl.enable({ emitEvent: false });
      return;
    }

    this.enableEmailEditControl.disable({ emitEvent: false });
    this.enablePasswordEditControl.disable({ emitEvent: false });
  }

  private updateEmailEditState(enabled: boolean, clearFeedback = true): void {
    if (!this.emailControl) {
      return;
    }

    if (enabled) {
      this.emailControl.enable({ emitEvent: false });
    } else {
      this.emailControl.disable({ emitEvent: false });
      this.emailControl.setValue(this.profile?.email ?? '', { emitEvent: false });
    }

    if (clearFeedback) {
      this.clearFeedback();
    }

    this.form.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  private updatePasswordEditState(enabled: boolean, clearFeedback = true): void {
    const currentPasswordControl = this.currentPasswordControl;
    const newPasswordControl = this.newPasswordControl;
    const confirmNewPasswordControl = this.confirmNewPasswordControl;

    if (!currentPasswordControl || !newPasswordControl || !confirmNewPasswordControl) {
      return;
    }

    if (!this.shouldShowPasswordFields) {
      currentPasswordControl.disable({ emitEvent: false });
      newPasswordControl.disable({ emitEvent: false });
      confirmNewPasswordControl.disable({ emitEvent: false });
      currentPasswordControl.setValue('', { emitEvent: false });
      newPasswordControl.setValue('', { emitEvent: false });
      confirmNewPasswordControl.setValue('', { emitEvent: false });
    } else if (enabled) {
      newPasswordControl.enable({ emitEvent: false });
      confirmNewPasswordControl.enable({ emitEvent: false });

      if (this.hasPasswordConfigured) {
        currentPasswordControl.enable({ emitEvent: false });
      } else {
        currentPasswordControl.disable({ emitEvent: false });
        currentPasswordControl.setValue('', { emitEvent: false });
      }
    } else {
      currentPasswordControl.disable({ emitEvent: false });
      newPasswordControl.disable({ emitEvent: false });
      confirmNewPasswordControl.disable({ emitEvent: false });
      currentPasswordControl.setValue('', { emitEvent: false });
      newPasswordControl.setValue('', { emitEvent: false });
      confirmNewPasswordControl.setValue('', { emitEvent: false });
    }

    if (clearFeedback) {
      this.clearFeedback();
    }

    this.form.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  private passwordChangeValidator(control: AbstractControl): ValidationErrors | null {
    const enablePasswordEdit = !!control.get('security.enablePasswordEdit')?.value;
    const currentPasswordControl = control.get('security.currentPassword');
    const newPasswordControl = control.get('security.newPassword');
    const confirmNewPasswordControl = control.get('security.confirmNewPassword');
    const currentPassword = control.get('security.currentPassword')?.value?.toString().trim() ?? '';
    const newPassword = control.get('security.newPassword')?.value?.toString().trim() ?? '';
    const confirmNewPassword = control.get('security.confirmNewPassword')?.value?.toString().trim() ?? '';

    this.toggleControlError(currentPasswordControl, 'currentPasswordRequired', false);
    this.toggleControlError(newPasswordControl, 'required', false);
    this.toggleControlError(confirmNewPasswordControl, 'required', false);
    this.toggleControlError(confirmNewPasswordControl, 'passwordMismatch', false);

    if (!enablePasswordEdit) {
      return null;
    }

    if (!this.shouldShowPasswordFields) {
      return null;
    }

    if (!newPassword && !confirmNewPassword && this.hasPasswordConfigured) {
      return null;
    }

    if (!newPassword) {
      this.toggleControlError(newPasswordControl, 'required', true);
    }

    if (!confirmNewPassword) {
      this.toggleControlError(confirmNewPasswordControl, 'required', true);
    }

    if (this.hasPasswordConfigured && !currentPassword) {
      this.toggleControlError(currentPasswordControl, 'currentPasswordRequired', true);
    }

    if (newPassword !== confirmNewPassword) {
      this.toggleControlError(confirmNewPasswordControl, 'passwordMismatch', true);
    }

    return currentPasswordControl?.errors || newPasswordControl?.errors || confirmNewPasswordControl?.errors
      ? { passwordChangeInvalid: true }
      : null;
  }

  private toggleControlError(control: AbstractControl | null, errorKey: string, shouldEnable: boolean): void {
    if (!control) {
      return;
    }

    const currentErrors = { ...(control.errors ?? {}) };

    if (shouldEnable) {
      if (currentErrors[errorKey]) {
        return;
      }

      currentErrors[errorKey] = true;
      control.setErrors(currentErrors);
      return;
    }

    if (!currentErrors[errorKey]) {
      return;
    }

    delete currentErrors[errorKey];
    control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
  }
}
